/**
 * @file `AdminTopNav` — admin's own top bar.
 * @module @labcraft/admin/ui/kernel/admin-top-nav
 *
 * Ported from the former shared `@labcraft/ui` `TopNav` when the page
 * chrome went per-app (#946): lab had already forked its bar
 * (`LabAppBar`), www built its own (`WwwTopNav`), and admin was the
 * last consumer — a single-consumer abstraction, so it now lives here.
 *
 * Trimmed to exactly what admin needs: the `lc>` mark + `Beta`
 * pill, a middle nav cluster, an optional `trailing` slot (admin's ⌘K
 * trigger), and the auth cluster — the shared {@link AccountMenu} when
 * signed in, or a "Sign in" button wired to the shared
 * {@link SignInPopover} (Google Workspace SSO only) when anonymous.
 * The brand mark, AccountMenu, and SignInPopover remain shared
 * primitives in `@labcraft/ui`.
 */

import { useRef, useState, type JSX } from 'react'
import {
  AccountMenu,
  LabCraftLogo,
  LAUNCHED,
  SignInPopover,
  type AccountMenuUser,
} from '@labcraft/ui'
import styles from './admin_top_nav.module.css'

/**
 * One middle-cluster nav item.
 *
 * @property label - Visible text.
 * @property href - Root-relative URL.
 * @property active - When `true`, renders `aria-current="page"` + active
 *   styling. The shell computes this from the current route.
 */
export interface AdminNavItem {
  label: string
  href: string
  active?: boolean
}

/**
 * Props for {@link AdminTopNav}.
 *
 * @property items - Middle nav cluster (empty until the operator signs in).
 * @property trailing - Optional element between the nav and the auth
 *   cluster — admin mounts its ⌘K command-palette trigger here.
 * @property authUser - When provided, the right cluster shows the shared
 *   {@link AccountMenu} instead of the sign-in button + popover.
 * @property onSignOut - Forwarded to {@link AccountMenu}.
 * @property onContinueWithGoogle - Starts the Workspace SSO flow from the
 *   anonymous sign-in popover.
 */
export interface AdminTopNavProps {
  items: AdminNavItem[]
  trailing?: JSX.Element
  authUser?: AccountMenuUser
  onSignOut: () => void
  onContinueWithGoogle?: () => void
}

/**
 * Admin's top bar. See {@link AdminTopNavProps}.
 *
 * @param props - See {@link AdminTopNavProps}.
 * @returns The top-nav header element.
 */
export function AdminTopNav(props: AdminTopNavProps): JSX.Element {
  const signInBtnRef = useRef<HTMLButtonElement | null>(null)
  const [signInOpen, setSignInOpen] = useState(false)

  return (
    <header className={styles.topNav}>
      <div className={styles.left}>
        <a className={styles.brand} href="/" aria-label="LabCraft home">
          <LabCraftLogo capH={22} />
        </a>
        {!LAUNCHED && <span className={styles.betaBadge}>Beta</span>}
      </div>
      <div className={styles.right}>
        <nav className={styles.nav} aria-label="Primary">
          {props.items.map(item => (
            <a
              key={item.href}
              className={
                item.active
                  ? `${styles.navLink} ${styles.navLinkActive}`
                  : styles.navLink
              }
              href={item.href}
              {...(item.active && { 'aria-current': 'page' })}>
              {item.label}
            </a>
          ))}
        </nav>
        {props.trailing}
        {props.authUser ? (
          <AccountMenu user={props.authUser} onSignOut={props.onSignOut} />
        ) : (
          <>
            <button
              ref={signInBtnRef}
              type="button"
              className={styles.signInBtn}
              aria-expanded={signInOpen}
              onClick={() => setSignInOpen(o => !o)}>
              Sign in
            </button>
            <SignInPopover
              anchorEl={signInBtnRef.current}
              open={signInOpen}
              onClose={() => setSignInOpen(false)}
              onContinueWithGoogle={props.onContinueWithGoogle}
            />
          </>
        )}
      </div>
    </header>
  )
}
